"use client";

import { Suspense, useEffect, useState, type ReactElement } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import {
  Alert,
  Box,
  Button,
  Autocomplete,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LinkIcon from "@mui/icons-material/Link";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import Footer from "@/components/layout/footer";
import theme from "@/theme/theme";
import SideTabbedButton from "@/components/generic/SideTabbedButton";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";

type AuthorRow = { _id: string; name: string };
type CategoryRow = { _id: string; title: string };

type CreateAuthorResponse =
  | { ok: true; author: AuthorRow; existed: boolean }
  | { ok: false; error: string };

type MetaResponse =
  | { ok: true; authors: ReadonlyArray<AuthorRow>; categories: ReadonlyArray<CategoryRow> }
  | { ok: false; error: string };

type CreateResponse =
  | {
      ok: true;
      id: string;
      slug: string;
      title: string;
      publishedAt: string;
      warnings: ReadonlyArray<string>;
      suggestedCategoryIds: string[];
      finalCategoryIds: string[];
      importDebug?: {
        docxBytes: number;
        nodeCounts: {
          raw: Record<string, number>;
          afterTitleStrip: Record<string, number>;
          afterDedupe: Record<string, number>;
          afterHydrate: Record<string, number>;
        };
        topLevelTypes: { raw: string[]; afterDedupe: string[] };
        topLevelImages: ReadonlyArray<{ index: number; fingerprint: string; mime?: string }>;
        topLevelTables: ReadonlyArray<{ index: number; rowCount: number; colCount: number; previewRows: string[][] }>;
        hydration: ReadonlyArray<Record<string, unknown>>;
        portableText: { total: number; blocks: number; images: number; tableImages: number; tables: number; breaks: number };
      };
    }
  | { ok: false; error: string };

type Status = "idle" | "loading" | "success" | "error";
type WizardStep = "author" | "upload";
type InputMode = "docx" | "richtext";

type Recaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, args: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Recaptcha;
  }
}

const inputSx = {
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.72)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.text.primary,
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "rgba(255,255,255,0.38)",
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: "transparent",
    borderRadius: 3,
    color: theme.palette.text.primary,
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
    "&.Mui-focused fieldset": { borderColor: theme.palette.brand.border1.main },
    "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.02)" },
    "&.Mui-disabled fieldset": { borderColor: "rgba(255,255,255,0.08)" },
  },
  "& .MuiInputBase-input": {
    color: theme.palette.text.primary,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "rgba(255,255,255,0.55)",
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255,255,255,0.58)",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: theme.palette.error.main,
  },
  "& .MuiSelect-icon": {
    color: "rgba(255,255,255,0.75)",
  },
} as const;

const darkAlertSx = {
  bgcolor: "rgba(13,16,22,0.9)",
  color: theme.palette.text.primary,
  border: "1px solid rgba(255,255,255,0.14)",
  "& .MuiAlert-icon": {
    color: "inherit",
  },
} as const;

function useRecaptchaV3(siteKey: string | undefined) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    const id = "recaptcha-v3";
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing) {
      setLoaded(true);
      return;
    }
    const s = document.createElement("script");
    s.id = id;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => setLoaded(true);
    s.onerror = () => setLoaded(false);
    document.head.appendChild(s);
  }, [siteKey]);

  const getToken = async (action: string): Promise<string> => {
    if (!siteKey) throw new Error("reCAPTCHA site key not configured");
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) throw new Error("reCAPTCHA not loaded yet");
    return await grecaptcha.execute(siteKey, { action });
  };

  return { loaded, getToken };
}

function importDebugFromSearchParams(params: URLSearchParams): boolean {
  const truthy = (v: string | null) => v === "1" || v?.toLowerCase() === "true" || v?.toLowerCase() === "yes";
  return (
    truthy(params.get("debug")) ||
    truthy(params.get("importDebug")) ||
    truthy(params.get("debugImport"))
  );
}

function hasRichTextContent(html: string): boolean {
  const normalized = html.replaceAll("&nbsp;", " ").trim().toLowerCase();
  if (normalized.includes("<img")) return true;
  const textOnly = normalized.replace(/<[^>]*>/g, " ").trim();
  return textOnly.length > 0;
}

function PortalPageInner(): ReactElement {
  const searchParams = useSearchParams();
  const searchKey = searchParams.toString();
  const router = useRouter();

  const [categories, setCategories] = useState<ReadonlyArray<CategoryRow>>([]);
  const [authors, setAuthors] = useState<ReadonlyArray<AuthorRow>>([]);

  const [author, setAuthor] = useState<AuthorRow | null>(null);
  const [selectedCategoryTitles, setSelectedCategoryTitles] = useState<ReadonlyArray<string>>([]);
  const [docx, setDocx] = useState<File | null>(null);
  const [inputMode, setInputMode] = useState<InputMode>("richtext");
  const [richHtml, setRichHtml] = useState<string>("");
  const [docxSourceUrl, setDocxSourceUrl] = useState<string>("");
  const [submitterEmail, setSubmitterEmail] = useState<string>("");
  const [newAuthorName, setNewAuthorName] = useState<string>("");
  const [newAuthorBio, setNewAuthorBio] = useState<string>("");
  const [newAuthorImage, setNewAuthorImage] = useState<File | null>(null);
  const [authorCreatedMessage, setAuthorCreatedMessage] = useState<string>("");
  const [importDebug, setImportDebug] = useState<boolean>(() =>
    importDebugFromSearchParams(new URLSearchParams(searchParams.toString()))
  );
  const aiProvider = "anthropic" as const;

  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const recaptcha = useRecaptchaV3(recaptchaSiteKey);

  useEffect(() => {
    setImportDebug(importDebugFromSearchParams(new URLSearchParams(searchKey)));
  }, [searchKey]);

  const [status, setStatus] = useState<Status>("idle");
  const [errorText, setErrorText] = useState<string>("");
  const [result, setResult] = useState<CreateResponse | null>(null);
  const [wizardStep, setWizardStep] = useState<WizardStep>("author");
  const [dialogOpen, setDialogOpen] = useState(false);

  const [, setEditorTick] = useState(0);

  const richEditor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: richHtml || "",
    onUpdate: ({ editor }) => {
      setRichHtml(editor.getHTML());
    },
    onSelectionUpdate: () => {
      setEditorTick((c) => c + 1);
    },
    editorProps: {
      attributes: {
        "data-placeholder": "Write or paste your content here. You can paste images directly.",
      },
      handlePaste: (_view, event) => {
        const e = event;
        const items = Array.from(e.clipboardData?.items ?? []);
        const images = items.filter((i) => i.type.startsWith("image/"));
        if (images.length === 0) return false;
        e.preventDefault();
        void (async () => {
          for (const item of images) {
            const file = item.getAsFile();
            if (!file) continue;
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
              reader.onerror = () => reject(new Error("Failed to read pasted image"));
              reader.readAsDataURL(file);
            });
            if (!dataUrl) continue;
            richEditor?.chain().focus().setImage({ src: dataUrl, alt: "Pasted image" }).run();
          }
        })();
        return true;
      },
    },
  });

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const res = await fetch("/api/portal/meta", { method: "GET", cache: "no-store" });
        const data = (await res.json()) as MetaResponse;
        if (!res.ok || !data.ok) throw new Error(!data.ok ? data.error : "Failed to load");
        if (!mounted) return;
        setCategories(data.categories);
        setAuthors(data.authors);
      } catch (e) {
        if (!mounted) return;
        setErrorText(e instanceof Error ? e.message : "Failed to load portal metadata");
        setStatus("error");
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submitterEmail.trim());
  const canSubmit =
    status !== "loading" &&
    Boolean(author) &&
    isValidEmail &&
    (inputMode === "docx" ? Boolean(docx) : hasRichTextContent(richHtml));
  const canCreateAuthor =
    status !== "loading" &&
    Boolean(newAuthorName.trim()) &&
    Boolean(newAuthorBio.trim()) &&
    Boolean(newAuthorImage) &&
    recaptcha.loaded;
  const [authorTriedSubmit, setAuthorTriedSubmit] = useState<boolean>(false);
  const showAuthorErrors = wizardStep === "author" && authorTriedSubmit;
  const nameError = showAuthorErrors && !newAuthorName.trim();
  const bioError = showAuthorErrors && !newAuthorBio.trim();
  const imageError = showAuthorErrors && !newAuthorImage;

  const onCreateAuthor = async () => {
    const name = newAuthorName.trim();
    const bio = newAuthorBio.trim();
    const image = newAuthorImage;
    if (!name || !bio || !image) return;
    setStatus("loading");
    setErrorText("");
    setResult(null);
    try {
      const recaptchaToken = await recaptcha.getToken("portal_create_author");
      const fd = new FormData();
      fd.set("name", name);
      fd.set("bio", bio);
      fd.set("image", image);
      fd.set("recaptchaToken", recaptchaToken);
      const res = await fetch("/api/portal/create-author", {
        method: "POST",
        body: fd,
      });
      const data = (await res.json()) as CreateAuthorResponse;
      if (!res.ok || !data.ok) throw new Error(!data.ok ? data.error : "Failed to create author");

      setAuthor(data.author);
      setAuthorCreatedMessage(`Author created successfully: ${data.author.name}`);
      setAuthors((prev) => {
        if (prev.some((a) => a._id === data.author._id)) return prev;
        return [...prev, data.author].sort((a, b) => a.name.localeCompare(b.name));
      });
      setNewAuthorName("");
      setNewAuthorBio("");
      setNewAuthorImage(null);
      setStatus("idle");
      setWizardStep("upload");
    } catch (e) {
      setErrorText(e instanceof Error ? e.message : "Failed to create author");
      setStatus("error");
    }
  };

  const onSubmit = async () => {
    if (!canSubmit || !author) return;

    setStatus("loading");
    setErrorText("");
    setResult(null);

    try {
      const recaptchaToken = await recaptcha.getToken("portal_upload_article");
      const fd = new FormData();
      fd.set("authorId", author._id);
      fd.set("sourceType", inputMode);
      if (inputMode === "docx") {
        if (!docx) throw new Error("Please upload a DOCX file.");
        fd.set("docx", docx);
      } else {
        if (!hasRichTextContent(richHtml)) throw new Error("Please provide rich text content.");
        fd.set("richHtml", richHtml);
      }
      fd.set("categoryTitles", JSON.stringify(selectedCategoryTitles));
      fd.set("fixTypos", "1");
      const docxSourceUrlTrimmed = docxSourceUrl.trim();
      if (docxSourceUrlTrimmed) fd.set("docxSourceUrl", docxSourceUrlTrimmed);
      const emailTrimmed = submitterEmail.trim();
      if (emailTrimmed) fd.set("submitterEmail", emailTrimmed);
      if (importDebug) fd.set("debugImport", "1");
      fd.set("aiProvider", aiProvider);
      fd.set("recaptchaToken", recaptchaToken);

      const res = await fetch("/api/portal/article-from-docx", { method: "POST", body: fd });
      const data = (await res.json()) as CreateResponse;
      if (!res.ok || !data.ok) throw new Error(!data.ok ? data.error : "Failed to create post");

      setResult(data);
      setStatus("success");
      setDocx(null);
      setRichHtml("");
      richEditor?.commands.clearContent();
      setDocxSourceUrl("");
      setSubmitterEmail("");
      setSelectedCategoryTitles([]);
      setDialogOpen(true);
    } catch (e) {
      setErrorText(e instanceof Error ? e.message : "Failed to create post");
      setStatus("error");
      setDialogOpen(true);
    }
  };

  const onSelectAuthor = (next: AuthorRow | null) => {
    setErrorText("");
    setResult(null);
    setStatus("idle");
    setAuthorCreatedMessage("");
    setAuthor(next);
  };

  const richToolbarVisible = wizardStep === "upload" && inputMode === "richtext";
  const richToolbarDisabled = status === "loading" || !richEditor;

  const richToolButtonSx = (active?: boolean) =>
    ({
      color: "rgba(255,255,255,0.86)",
      bgcolor: active ? "rgba(78, 190, 255, 0.18)" : "transparent",
      borderRadius: 1.5,
      "&:hover": {
        bgcolor: active ? "rgba(78, 190, 255, 0.24)" : "rgba(255,255,255,0.08)",
      },
      "&.Mui-disabled": {
        color: "rgba(255,255,255,0.30)",
      },
    }) as const;

  const onToggleLink = () => {
    const ed = richEditor;
    if (!ed) return;
    const prev = ed.getAttributes("link")?.href as string | undefined;
    const next = window.prompt("Link URL", prev ?? "https://");
    if (!next) {
      ed.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    ed.chain().focus().extendMarkRange("link").setLink({ href: next }).run();
  };

  const onInlineImagePick = async (file: File | null) => {
    const ed = richEditor;
    if (!ed || !file) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => reject(new Error("Failed to read image file"));
      reader.readAsDataURL(file);
    });
    if (!dataUrl) return;
    ed.chain().focus().setImage({ src: dataUrl, alt: file.name || "Inline image" }).run();
  };

  const onBack = () => {
    if (status === "loading") return;
    setErrorText("");
    setResult(null);
    if (wizardStep === "upload") setWizardStep("author");
  };

  const onNext = () => {
    if (wizardStep === "author") {
      setWizardStep("upload");
      return;
    }
  };

  const onPrimarySubmit = async () => {
    if (wizardStep === "author") {
      setAuthorTriedSubmit(true);
      await onCreateAuthor();
      return;
    }
    if (wizardStep === "upload") {
      await onSubmit();
    }
  };

  const backDisabled = status === "loading" || wizardStep === "author";
  const nextVisible = wizardStep === "author";
  const submitVisible = wizardStep === "author" || wizardStep === "upload";

  const nextDisabled = status === "loading";

  const submitDisabled =
    status === "loading" ||
    (wizardStep === "author" ? !canCreateAuthor : wizardStep === "upload" ? !canSubmit : true);

  return (
    <Stack width="100%" alignItems="center">
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={3}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Article Upload Portal
          </Typography>

          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.10)",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {wizardStep === "author"
                      ? "Step 1: Create author (optional)"
                      : "Step 2: Upload article"}
                  </Typography>

                  {wizardStep === "author" ? (
                    <Tooltip title="If you already have added these details, you can skip and select yourself later.">
                      <IconButton
                        size="small"
                        sx={{ color: "rgba(255,255,255,0.75)", ml: 0.25 }}
                        aria-label="Author step info"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>

                {wizardStep === "author" ? (
                  <Stack spacing={1.5}>
                    {author ? (
                      <Alert severity="success" sx={darkAlertSx}>
                        Current author selected: {author.name}. You can still create a new one below.
                      </Alert>
                    ) : null}
                    {authorCreatedMessage ? (
                      <Alert severity="success" sx={darkAlertSx}>
                        {authorCreatedMessage}
                      </Alert>
                    ) : null}

                    <TextField
                      label="Name"
                      value={newAuthorName}
                      onChange={(e) => setNewAuthorName(e.target.value)}
                      sx={inputSx}
                      fullWidth
                      required
                      error={nameError}
                      helperText={nameError ? "Name is required." : " "}
                      disabled={status === "loading"}
                    />

                    <TextField
                      label="Bio"
                      value={newAuthorBio}
                      onChange={(e) => setNewAuthorBio(e.target.value)}
                      sx={inputSx}
                      fullWidth
                      required
                      multiline
                      minRows={3}
                      error={bioError}
                      helperText={bioError ? "Bio is required." : " "}
                      disabled={status === "loading"}
                    />

                    <Stack spacing={0.75}>
                      {imageError ? (
                        <Typography variant="caption" sx={{ color: theme.palette.error.main }}>
                          Image is required.
                        </Typography>
                      ) : null}
                      <Box
                        sx={{
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 2,
                          p: 2,
                          bgcolor: "transparent",
                        }}
                      >
                        <input
                          id="author-image-upload"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setNewAuthorImage(e.target.files?.[0] ?? null)}
                          disabled={status === "loading"}
                          style={{ display: "none" }}
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                          <Box
                            component="label"
                            htmlFor="author-image-upload"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              py: 1,
                              borderRadius: 2,
                              cursor: status === "loading" ? "not-allowed" : "pointer",
                              userSelect: "none",
                              border: "1px solid rgba(255,255,255,0.12)",
                              bgcolor: "rgba(255,255,255,0.04)",
                              "&:hover": status === "loading" ? undefined : { borderColor: "rgba(255,255,255,0.24)" },
                              opacity: status === "loading" ? 0.6 : 1,
                              width: { xs: "100%", sm: "fit-content" },
                              justifyContent: "center",
                            }}
                          >
                            <IconButton
                              size="small"
                              disableRipple
                              sx={{ color: theme.palette.text.primary, p: 0, "&:hover": { bgcolor: "transparent" } }}
                              aria-label="Upload author image"
                            >
                              <UploadFileIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                              Upload image
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            {newAuthorImage ? `Selected: ${newAuthorImage.name}` : "No file selected"}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>

                  </Stack>
                ) : null}

                {wizardStep === "upload" ? (
                  <Stack spacing={1.5}>
                    <Autocomplete
                      options={authors}
                      getOptionLabel={(o) => o.name}
                      value={author}
                      onChange={(_, v) => {
                        onSelectAuthor(v);
                      }}
                      disabled={status === "loading"}
                      sx={{
                        "& .MuiAutocomplete-popupIndicator": {
                          color: "rgba(255,255,255,0.75)",
                        },
                        "& .MuiAutocomplete-clearIndicator": {
                          color: "rgba(255,255,255,0.75)",
                        },
                        "& .MuiAutocomplete-popupIndicator:hover, & .MuiAutocomplete-clearIndicator:hover": {
                          color: "rgba(255,255,255,0.92)",
                        },
                        "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
                          bgcolor: "transparent",
                        },
                      }}
                      slotProps={{
                        popper: {
                          sx: {
                            "& .MuiPaper-root": {
                              bgcolor: "rgba(10,10,12,0.96)",
                              color: theme.palette.text.primary,
                              border: "1px solid rgba(255,255,255,0.14)",
                              boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
                              backdropFilter: "blur(10px)",
                            },
                            "& .MuiAutocomplete-option": {
                              color: theme.palette.text.primary,
                            },
                            "& .MuiAutocomplete-option[aria-selected='true']": {
                              bgcolor: "rgba(78, 190, 255, 0.18)",
                            },
                            "& .MuiAutocomplete-option.Mui-focused": {
                              bgcolor: "rgba(255,255,255,0.08)",
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Author"
                          placeholder="Select author"
                          sx={inputSx}
                          fullWidth
                        />
                      )}
                    />

                    <Autocomplete
                      multiple
                      freeSolo
                      options={categories.map((c) => c.title)}
                      value={[...selectedCategoryTitles]}
                      onChange={(_, v) => {
                        const next = (v ?? [])
                          .map((s) => String(s).trim())
                          .filter(Boolean);
                        setSelectedCategoryTitles(next);
                      }}
                      disabled={status === "loading"}
                      sx={{
                        "& .MuiAutocomplete-popupIndicator": {
                          color: "rgba(255,255,255,0.75)",
                        },
                        "& .MuiAutocomplete-clearIndicator": {
                          color: "rgba(255,255,255,0.75)",
                        },
                        "& .MuiAutocomplete-popupIndicator:hover, & .MuiAutocomplete-clearIndicator:hover": {
                          color: "rgba(255,255,255,0.92)",
                        },
                        "& .MuiAutocomplete-tag .MuiChip-deleteIcon": {
                          color: "rgba(255,255,255,0.65)",
                        },
                        "& .MuiAutocomplete-tag .MuiChip-deleteIcon:hover": {
                          color: "rgba(255,255,255,0.92)",
                        },
                        "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
                          bgcolor: "transparent",
                        },
                      }}
                      slotProps={{
                        popper: {
                          sx: {
                            "& .MuiPaper-root": {
                              bgcolor: "rgba(10,10,12,0.96)",
                              color: theme.palette.text.primary,
                              border: "1px solid rgba(255,255,255,0.14)",
                              boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
                              backdropFilter: "blur(10px)",
                            },
                            "& .MuiAutocomplete-option": {
                              color: theme.palette.text.primary,
                            },
                            "& .MuiAutocomplete-option[aria-selected='true']": {
                              bgcolor: "rgba(78, 190, 255, 0.18)",
                            },
                            "& .MuiAutocomplete-option.Mui-focused": {
                              bgcolor: "rgba(255,255,255,0.08)",
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Categories"
                          placeholder="Select or type to add"
                          sx={inputSx}
                          fullWidth
                        />
                      )}
                    />

                    <TextField
                      select
                      label="Content source"
                      value={inputMode}
                      onChange={(e) => setInputMode(e.target.value as InputMode)}
                      sx={inputSx}
                      fullWidth
                      disabled={status === "loading"}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              bgcolor: "rgba(10,10,12,0.96)",
                              color: theme.palette.text.primary,
                              border: "1px solid rgba(255,255,255,0.14)",
                              boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
                              backdropFilter: "blur(10px)",
                              "& .MuiMenuItem-root": {
                                color: theme.palette.text.primary,
                              },
                              "& .MuiMenuItem-root.Mui-selected": {
                                bgcolor: "rgba(78, 190, 255, 0.18)",
                              },
                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                bgcolor: "rgba(78, 190, 255, 0.24)",
                              },
                              "& .MuiMenuItem-root:hover": {
                                bgcolor: "rgba(255,255,255,0.08)",
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="richtext">Rich text editor</MenuItem>
                      <MenuItem value="docx">DOCX file upload</MenuItem>
                    </TextField>

                    <TextField
                      label="Source link (optional)"
                      placeholder="https://example.com/article-source"
                      value={docxSourceUrl}
                      onChange={(e) => setDocxSourceUrl(e.target.value)}
                      sx={inputSx}
                      fullWidth
                      type="url"
                      helperText="Optional URL used by reviewers to trace the original source."
                      disabled={status === "loading"}
                    />

                    <TextField
                      label="Your email"
                      placeholder="you@example.com"
                      value={submitterEmail}
                      onChange={(e) => setSubmitterEmail(e.target.value)}
                      sx={inputSx}
                      fullWidth
                      required
                      type="email"
                      helperText="You will receive an email when your article is approved and goes live."
                      disabled={status === "loading"}
                    />

                    {inputMode === "docx" ? (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">DOCX file</Typography>
                        <Box
                          sx={{
                            border: "1px solid rgba(255,255,255,0.12)",
                            borderRadius: 2,
                            p: 2,
                            bgcolor: "transparent",
                          }}
                        >
                          <input
                            id="docx-upload"
                            type="file"
                            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={(e) => setDocx(e.target.files?.[0] ?? null)}
                            disabled={status === "loading"}
                            style={{ display: "none" }}
                          />

                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                            <Box
                              component="label"
                              htmlFor="docx-upload"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                cursor: status === "loading" ? "not-allowed" : "pointer",
                                userSelect: "none",
                                border: "1px solid rgba(255,255,255,0.12)",
                                bgcolor: "rgba(255,255,255,0.04)",
                                "&:hover": status === "loading" ? undefined : { borderColor: "rgba(255,255,255,0.24)" },
                                opacity: status === "loading" ? 0.6 : 1,
                                width: { xs: "100%", sm: "fit-content" },
                                justifyContent: "center",
                              }}
                            >
                              <IconButton
                                size="small"
                                disableRipple
                                sx={{ color: theme.palette.text.primary, p: 0, "&:hover": { bgcolor: "transparent" } }}
                                aria-label="Upload DOCX"
                              >
                                <UploadFileIcon fontSize="small" />
                              </IconButton>
                              <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                                Upload DOCX
                              </Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              {docx ? `Selected: ${docx.name} (${Math.round(docx.size / 1024)} KB)` : "No file selected"}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    ) : (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Rich text content</Typography>
                        {richToolbarVisible ? (
                          <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={0.5}
                            sx={{
                              border: "1px solid rgba(255,255,255,0.12)",
                              borderRadius: 2,
                              p: 0.75,
                              bgcolor: "rgba(255,255,255,0.02)",
                            }}
                          >
                            <Tooltip title="Bold">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleBold().run()}
                                  onClick={() => richEditor?.chain().focus().toggleBold().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("bold"))}
                                  aria-label="Bold"
                                >
                                  <FormatBoldIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Italic">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleItalic().run()}
                                  onClick={() => richEditor?.chain().focus().toggleItalic().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("italic"))}
                                  aria-label="Italic"
                                >
                                  <FormatItalicIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Underline">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleUnderline().run()}
                                  onClick={() => richEditor?.chain().focus().toggleUnderline().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("underline"))}
                                  aria-label="Underline"
                                >
                                  <FormatUnderlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Strikethrough">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleStrike().run()}
                                  onClick={() => richEditor?.chain().focus().toggleStrike().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("strike"))}
                                  aria-label="Strikethrough"
                                >
                                  <StrikethroughSIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Box sx={{ width: 1, height: 1, mx: 0.5 }} />

                            <Tooltip title="Bulleted list">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleBulletList().run()}
                                  onClick={() => richEditor?.chain().focus().toggleBulletList().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("bulletList"))}
                                  aria-label="Bulleted list"
                                >
                                  <FormatListBulletedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Numbered list">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleOrderedList().run()}
                                  onClick={() => richEditor?.chain().focus().toggleOrderedList().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("orderedList"))}
                                  aria-label="Numbered list"
                                >
                                  <FormatListNumberedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Block quote">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleBlockquote().run()}
                                  onClick={() => richEditor?.chain().focus().toggleBlockquote().run()}
                                  sx={richToolButtonSx(richEditor?.isActive("blockquote"))}
                                  aria-label="Block quote"
                                >
                                  <FormatQuoteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Heading (H2)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleHeading({ level: 2 }).run()}
                                  onClick={() => richEditor?.chain().focus().toggleHeading({ level: 2 }).run()}
                                  sx={richToolButtonSx(richEditor?.isActive("heading", { level: 2 }))}
                                  aria-label="Heading H2"
                                >
                                  <Box component="span" sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>H2</Box>
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Heading (H3)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleHeading({ level: 3 }).run()}
                                  onClick={() => richEditor?.chain().focus().toggleHeading({ level: 3 }).run()}
                                  sx={richToolButtonSx(richEditor?.isActive("heading", { level: 3 }))}
                                  aria-label="Heading H3"
                                >
                                  <Box component="span" sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>H3</Box>
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Heading (H4)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled || !richEditor?.can().chain().focus().toggleHeading({ level: 4 }).run()}
                                  onClick={() => richEditor?.chain().focus().toggleHeading({ level: 4 }).run()}
                                  sx={richToolButtonSx(richEditor?.isActive("heading", { level: 4 }))}
                                  aria-label="Heading H4"
                                >
                                  <Box component="span" sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>H4</Box>
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Link">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={richToolbarDisabled}
                                  onClick={onToggleLink}
                                  sx={richToolButtonSx(richEditor?.isActive("link"))}
                                  aria-label="Link"
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Insert image">
                              <span>
                                <input
                                  id="portal-rich-inline-image"
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => void onInlineImagePick(e.target.files?.[0] ?? null)}
                                  disabled={richToolbarDisabled}
                                  style={{ display: "none" }}
                                />
                                <IconButton
                                  component="label"
                                  htmlFor="portal-rich-inline-image"
                                  size="small"
                                  disabled={richToolbarDisabled}
                                  sx={richToolButtonSx(false)}
                                  aria-label="Insert image"
                                >
                                  <AddPhotoAlternateIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : null}
                        <Box
                          onClick={() => richEditor?.commands.focus()}
                          sx={{
                            minHeight: 280,
                            maxHeight: "70vh",
                            border: "1px solid rgba(255,255,255,0.12)",
                            borderRadius: 2,
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: "rgba(255,255,255,0.02)",
                            color: theme.palette.text.primary,
                            overflow: "auto",
                            outline: "none",
                            cursor: "text",
                            "&:focus-within": {
                              borderColor: theme.palette.brand.border1.main,
                            },
                            "& .ProseMirror": {
                              outline: "none",
                              minHeight: 240,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              caretColor: theme.palette.brand.mgsBlue2.main,
                              fontSize: "16px",
                              lineHeight: 1.8,
                              color: "#ABABAB",
                            },
                            "& .ProseMirror ::selection": {
                              background: "rgba(78, 190, 255, 0.35)",
                            },
                            "& .ProseMirror > * + *": {
                              marginTop: theme.spacing(1.25),
                            },
                            "& .ProseMirror p": { margin: 0 },
                            "& .ProseMirror h1, & .ProseMirror h2": {
                              margin: 0,
                              marginTop: theme.spacing(2),
                              fontSize: "1.7rem",
                              fontWeight: 400,
                              fontFamily: '"Funnel Display", sans-serif',
                              lineHeight: 1.2,
                              color: "#1793FA !important",
                            },
                            "& .ProseMirror h3": {
                              margin: 0,
                              marginTop: theme.spacing(1.5),
                              fontSize: "1.35rem",
                              fontWeight: 600,
                              fontFamily: '"Funnel Display", sans-serif',
                              lineHeight: 1.2,
                              color: "#FFFFFF !important",
                            },
                            "& .ProseMirror h4": {
                              margin: 0,
                              marginTop: theme.spacing(1.5),
                              fontSize: "1.1rem",
                              fontWeight: 600,
                              fontFamily: '"Funnel Display", sans-serif',
                              lineHeight: 1.2,
                              color: "#1793FA !important",
                            },
                            "& .ProseMirror strong": {
                              fontWeight: 600,
                              color: "#FFFFFF",
                            },
                            "& .ProseMirror em": {
                              fontStyle: "italic",
                              color: "#CCCCCC",
                            },
                            "& .ProseMirror u": {
                              textDecorationThickness: "2px",
                              textUnderlineOffset: "3px",
                              textDecorationColor: "rgba(78, 190, 255, 0.75)",
                            },
                            "& .ProseMirror s": {
                              textDecorationThickness: "2px",
                              textDecorationColor: "rgba(255,255,255,0.55)",
                            },
                            "& .ProseMirror a": {
                              color: "rgba(78, 190, 255, 0.95)",
                              textDecoration: "underline",
                              textDecorationThickness: "2px",
                              textUnderlineOffset: "3px",
                              textDecorationColor: "rgba(78, 190, 255, 0.65)",
                            },
                            "& .ProseMirror ul": {
                              margin: 0,
                              paddingLeft: theme.spacing(2.5),
                              listStyle: "none",
                            },
                            "& .ProseMirror ul li": {
                              position: "relative",
                              paddingLeft: theme.spacing(2),
                            },
                            "& .ProseMirror ul li::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: "0.55em",
                              width: 6,
                              height: 6,
                              backgroundColor: "#1793FA",
                              borderRadius: "1px",
                            },
                            "& .ProseMirror ol": {
                              margin: 0,
                              paddingLeft: theme.spacing(3),
                              listStyleType: "decimal",
                            },
                            "& .ProseMirror ol li::marker": {
                              color: "rgba(255,255,255,0.82) !important",
                            },
                            "& .ProseMirror li": {
                              marginTop: theme.spacing(0.5),
                            },
                            "& .ProseMirror li p": {
                              margin: 0,
                            },
                            "& .ProseMirror blockquote": {
                              margin: theme.spacing(6, 0),
                              padding: 0,
                              paddingLeft: theme.spacing(3),
                              borderLeft: "2px solid #1793FA",
                              fontStyle: "italic",
                              fontWeight: 600,
                              fontSize: "18px",
                              lineHeight: 1.5,
                              color: "#ABABAB",
                            },
                            "& .ProseMirror blockquote p": {
                              margin: 0,
                            },
                            "& .ProseMirror table": {
                              borderCollapse: "collapse",
                              width: "100%",
                              margin: theme.spacing(1.5, 0),
                              fontSize: "15px",
                              lineHeight: 1.6,
                            },
                            "& .ProseMirror th, & .ProseMirror td": {
                              border: "1px solid rgba(255,255,255,0.12)",
                              padding: theme.spacing(1.5, 2),
                              verticalAlign: "top",
                              textAlign: "left",
                              color: "#ABABAB",
                            },
                            "& .ProseMirror th, & .ProseMirror tr:first-of-type td, & .ProseMirror tr:first-of-type th": {
                              backgroundColor: "rgba(23, 147, 250, 0.08)",
                              color: "#1793FA",
                              fontWeight: 600,
                              fontSize: "14px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            },
                            "& .ProseMirror tr:nth-of-type(even) td": {
                              backgroundColor: "rgba(255,255,255,0.02)",
                            },
                            "& .ProseMirror hr": {
                              border: "none",
                              borderTop: "1px solid rgba(255,255,255,0.12)",
                              margin: theme.spacing(2, 0),
                            },
                            "& .ProseMirror img": {
                              maxWidth: "100%",
                              height: "auto",
                              borderRadius: 1,
                              display: "block",
                              marginTop: theme.spacing(1.5),
                              marginBottom: theme.spacing(1.5),
                            },
                            "& .ProseMirror.ProseMirror-focused": {
                              outline: "none",
                            },
                            "& .ProseMirror p.is-editor-empty:first-of-type::before": {
                              content: "attr(data-placeholder)",
                              float: "left",
                              color: "rgba(255,255,255,0.5)",
                              pointerEvents: "none",
                              height: 0,
                            },
                          }}
                        >
                          <EditorContent editor={richEditor} />
                        </Box>
                      </Stack>
                    )}
                  </Stack>
                ) : null}

              </Stack>
            </CardContent>

            <CardActions
              sx={{
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 3 },
                pt: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
                {wizardStep !== "author" ? (
                  <Button variant="text" onClick={onBack} disabled={backDisabled}>
                    Back
                  </Button>
                ) : null}
              </Stack>

              <Stack direction="row" alignItems="center" gap={1} sx={{ justifyContent: "flex-end", flex: 1 }}>
                {nextVisible ? (
                  <Button
                    variant="outlined"
                    onClick={onNext}
                    disabled={nextDisabled}
                    sx={{ borderRadius: 2, px: 3, py: 1.25, minWidth: 120 }}
                  >
                    {wizardStep === "author" && !author ? "Skip" : "Next"}
                  </Button>
                ) : null}

                {submitVisible ? (
                  <Box sx={{ ml: wizardStep === "upload" ? 0 : 0 }}>
                    <SideTabbedButton
                      action={() => void onPrimarySubmit()}
                      disabled={submitDisabled}
                      height={44}
                      paddingX={18}
                      shadowColor={String(theme.palette.brand.mgsBlue2.main)}
                    >
                      {status === "loading" ? "Submitting…" : "Submit"}
                    </SideTabbedButton>
                  </Box>
                ) : null}
              </Stack>
            </CardActions>
          </Card>
        </Stack>
      </Container>

      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setStatus("idle"); }}
        PaperProps={{
          sx: {
            bgcolor: "rgba(13,16,22,0.96)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 3,
            boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            backdropFilter: "blur(12px)",
            maxWidth: 440,
            width: "100%",
            p: 0,
          },
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" } },
        }}
      >
        <DialogContent sx={{ p: { xs: 3, sm: 4 }, textAlign: "center" }}>
          {status === "success" && result?.ok ? (
            <Stack spacing={2.5} alignItems="center">
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#4caf50" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Thank you for your submission!
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>
                Your article <strong style={{ color: "#fff" }}>{result.title}</strong> has been created successfully.
              </Typography>
              {(result.warnings?.length ?? 0) > 0 ? (
                <Alert severity="warning" sx={{ ...darkAlertSx, width: "100%", textAlign: "left" }}>
                  {result.warnings.map((w, i) => <Typography key={i} variant="caption" display="block">{w}</Typography>)}
                </Alert>
              ) : null}
              <Button
                variant="outlined"
                onClick={() => { setDialogOpen(false); setStatus("idle"); router.push("/"); }}
                sx={{ mt: 1, borderRadius: 2, px: 4, textTransform: "none" }}
              >
                Close
              </Button>
            </Stack>
          ) : status === "error" ? (
            <Stack spacing={2.5} alignItems="center">
              <ErrorOutlineIcon sx={{ fontSize: 56, color: theme.palette.error.main }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Something went wrong
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>
                {errorText || "An unexpected error occurred. Please try again."}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => { setDialogOpen(false); setStatus("idle"); }}
                sx={{ mt: 1, borderRadius: 2, px: 4, textTransform: "none" }}
              >
                Close
              </Button>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      <Stack
        width={"100%"}
        alignItems={"center"}
        position={"relative"}
        sx={{
          borderTop: `1px solid ${theme.palette.brand.border1.main}`,
          marginTop: 6,
        }}
      >
        <Footer />
      </Stack>
    </Stack>
  );
}

function PortalPageFallback(): ReactElement {
  return (
    <Stack width="100%" alignItems="center" sx={{ minHeight: "100vh" }}>
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }} />
    </Stack>
  );
}

export default function PortalPage(): ReactElement {
  return (
    <Suspense fallback={<PortalPageFallback />}>
      <PortalPageInner />
    </Suspense>
  );
}

